import { Box, Typography } from '@mui/material';
import { Scatter, XAxis, YAxis, Tooltip, ResponsiveContainer, Line, ComposedChart } from 'recharts';
import { CompoundCardProps } from '../../types/reports';
import { ABOVE_RANGE, BELOW_RANGE, DETECTED, INCONCLUSIVE, NOT_DETECTED } from '../../constants/report';
import { getDisplayLabel, isNotesOrDisclaimer } from '../../utils/reports';
import { useThemeContext } from '../../contexts/BaseThemeContextProvider';

interface ChartDataPoint {
  name: string;
  x: number;
  y: number;
  originalData: CompoundCardProps;
}

type CompoundLevelsBubbleChartProps = {
  compoundDataList: CompoundCardProps[];
};

const CompoundLevelsBubbleChart = ({ compoundDataList }: CompoundLevelsBubbleChartProps) => {
  const { theme } = useThemeContext();
  // Transform data for the chart
  const decoratedData: ChartDataPoint[] = compoundDataList
    .filter((item) => {
      const { testNameAbbr, testUnit } = item.ddfv;
      return !isNotesOrDisclaimer(testNameAbbr, testUnit);
    })
    .map((item, _index) => ({
      // shorter version
      name: item.ddfv.testNameAbbr?.slice(0, 4),
      x: parseFloat(item.value || '') || 0,
      y: -1,
      originalData: item,
    }))
    .sort((a, b) => b.x - a.x);

  // This buffer is just a trick to make the chart expand beyond the first and last points
  // and have elements look like they have space at the start/end of the chart
  const firstElement = { ...decoratedData[0], name: '' };
  const lastElement = { ...decoratedData[decoratedData.length - 1], name: '' };
  const chartData = [
    { ...firstElement, x: decoratedData[0].x - 1, isBuffer: true },
    ...decoratedData,
    {
      ...lastElement,
      x: decoratedData[decoratedData.length - 1].x + 1,
      isBuffer: true,
    },
  ];
  const DEFAULT_X_SCATTER_POSITION = -13;
  const DEFAULT_Y_SCATTER_POSITION = -5;
  const DEFAULT_SCATTER_WIDTH = 15;
  const DEFAULT_SCATTER_HEIGHT = 10;

  // Calculate maximum value for proper scaling
  const maxValue = Math.max(
    ...chartData.filter((item) => item.originalData.ddfv.label === DETECTED).map((item) => item.x),
  );

  const getIconPath = (ddfvLabel: string): string => {
    switch (ddfvLabel) {
      case NOT_DETECTED:
        return theme.custom.report.bubbleViewContent.compoundLevelsBubbleChart.notDetectedIcon;
      case BELOW_RANGE:
        return theme.custom.report.bubbleViewContent.compoundLevelsBubbleChart.belowRangeIcon;
      case ABOVE_RANGE:
        return theme.custom.report.bubbleViewContent.compoundLevelsBubbleChart.aboveRangeIcon;
      case INCONCLUSIVE:
        return theme.custom.report.bubbleViewContent.compoundLevelsBubbleChart.inconclusiveIcon;
      default:
        return '';
    }
  };

  // Custom scatter point component
  const CustomScatterPoint = (props: any) => {
    const { cx, cy, payload } = props;
    const { originalData } = payload;
    const { label } = originalData.ddfv;
    if (payload.isBuffer) return null;

    // Calculate radius based on value
    const baseRadius = 20;
    const radius =
      label === DETECTED ? Math.max(baseRadius * (parseFloat(originalData.value) / maxValue), 10) : baseRadius;
    const yPosition = label === ABOVE_RANGE ? DEFAULT_Y_SCATTER_POSITION - 20 : DEFAULT_Y_SCATTER_POSITION;
    const imageWidth = label === ABOVE_RANGE ? 30 : DEFAULT_SCATTER_WIDTH;
    const imageHeight = label === ABOVE_RANGE ? 25 : DEFAULT_SCATTER_HEIGHT;

    return (
      <g transform={`translate(${cx},${cy})`}>
        {label === DETECTED ? (
          <circle
            r={radius}
            fill={
              theme.custom.report.bubbleViewContent.compoundLevelsBubbleChart.customScatterPoint.detected.circle
                .fillColor
            }
            fillOpacity={0.8}
          />
        ) : (
          <image
            href={getIconPath(label)}
            x={DEFAULT_X_SCATTER_POSITION}
            y={yPosition}
            width={imageWidth}
            height={imageHeight}
          />
        )}
      </g>
    );
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.[0]) return <></>;

    const { originalData } = payload[0].payload;
    const { label, value } = originalData.ddfv;
    let displayedValue = value;
    if ([ABOVE_RANGE, BELOW_RANGE, NOT_DETECTED, INCONCLUSIVE].includes(label)) displayedValue = '---';
    const displayLabel = getDisplayLabel(originalData.ddfv.label);
    return (
      <Box
        sx={{
          bgcolor: theme.custom.report.bubbleViewContent.compoundLevelsBubbleChart.customTooltip.backgroundColor,
          color: theme.custom.report.bubbleViewContent.compoundLevelsBubbleChart.customTooltip.color,
          p: 1.5,
          border: 1,
          borderColor: 'divider',
          borderRadius: 4,
          boxShadow: 1,
          minWidth: 200,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 'medium',
            mb: 0.5,
            color:
              theme.custom.report.bubbleViewContent.compoundLevelsBubbleChart.customTooltip.typography.subtitle2.color,
          }}
        >
          {originalData.ddfv.testNameAbbr}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.custom.report.bubbleViewContent.compoundLevelsBubbleChart.customTooltip.typography.body2.color,
          }}
        >
          Value: {displayedValue}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.custom.report.bubbleViewContent.compoundLevelsBubbleChart.customTooltip.typography.body2.color,
          }}
        >
          Status: {displayLabel}
        </Typography>
      </Box>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={100}>
      <ComposedChart data={chartData} margin={{ top: 0, right: 10, bottom: 0, left: -40 }}>
        <XAxis
          type="category"
          dataKey="name"
          name="value"
          height={20}
          tick={{
            fontSize: 12,
            stroke:
              theme.custom.report.bubbleViewContent.compoundLevelsBubbleChart.composedChart.xAxis.tick.strokeColor,
          }}
          tickLine={{ visibility: 'hidden' }}
          axisLine={{
            stroke:
              theme.custom.report.bubbleViewContent.compoundLevelsBubbleChart.composedChart.xAxis.axisLine.strokeColor,
            strokeWidth: 1.5,
          }}
          label={{
            value: '0',
            position: 'insideTopLeft',
            dx: -10,
            offset: -5,
          }}
        />
        <YAxis type="number" dataKey="y" name="title" height={120} tick={false} tickLine={false} axisLine={false} />
        <Tooltip cursor={{ strokeDasharray: '3 3 ', strokeWidth: 0 }} content={<CustomTooltip />} />

        <Line
          dataKey="y"
          stroke={theme.custom.report.bubbleViewContent.compoundLevelsBubbleChart.composedChart.line.strokeColor}
          dot={false}
          activeDot={false}
          strokeWidth={1}
          style={{
            pointerEvents: 'none',
          }}
        />
        <Scatter dataKey="y" strokeDasharray="3 3" shape={<CustomScatterPoint />} isAnimationActive={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default CompoundLevelsBubbleChart;
